import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { CalendarToday as CalendarIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import api from '../api';
import {
  asArray,
  formatSlotDateTime,
  formatSlotTime,
  groupSlotsByDay,
  parseBookingSearch,
  propertyLabel,
} from '../utils/showings';

// MUI Select renders an empty-string value as blank, so "whole property" needs a real value.
const ANY_UNIT = 'any';

/**
 * Public prospect booking — Calendly-style: property → day → time → details.
 * Requires ?l=<landlordSlug> (or legacy ?propertyId= for a single property).
 */
const ShowingBooking = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { bookingSlug, propertyId: queryPropertyId } = parseBookingSearch(location.search);

  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [landlordName, setLandlordName] = useState('');
  const [properties, setProperties] = useState([]);
  const [timeZone, setTimeZone] = useState('');
  const [showingMinutes, setShowingMinutes] = useState(60);

  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [booked, setBooked] = useState(null);

  const [formData, setFormData] = useState({
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    notes: '',
  });

  const loadPage = useCallback(async () => {
    setPageLoading(true);
    setPageError(null);
    try {
      const params = new URLSearchParams();
      if (bookingSlug) params.set('l', bookingSlug);
      if (queryPropertyId) params.set('propertyId', queryPropertyId);
      const res = await api.get(`/api/showings/public/page?${params.toString()}`);
      const props = asArray(res.data?.properties);
      setLandlordName(res.data?.landlordName || 'Your property manager');
      setProperties(props);
      setTimeZone(res.data?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      setShowingMinutes(res.data?.showingMinutes || 60);
      const preselect = res.data?.selectedPropertyId || (props.length === 1 ? props[0].id : '');
      setSelectedProperty(preselect || '');
    } catch (err) {
      setPageError(
        err.response?.data?.error ||
          'This booking link is not valid. Ask the property manager for their link.'
      );
    } finally {
      setPageLoading(false);
    }
  }, [bookingSlug, queryPropertyId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const selectedPropertyData = useMemo(
    () => properties.find((p) => p.id === selectedProperty) || null,
    [properties, selectedProperty]
  );
  const units = useMemo(() => asArray(selectedPropertyData?.units), [selectedPropertyData]);

  const fetchSlots = useCallback(async () => {
    if (!selectedProperty) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    setError(null);
    try {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
      const params = new URLSearchParams({
        propertyId: selectedProperty,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      });
      if (selectedUnit) params.set('unitId', selectedUnit);
      const res = await api.get(`/api/showings/public/available?${params.toString()}`);
      setSlots(asArray(res.data?.availableSlots ?? res.data));
      if (res.data?.timeZone) setTimeZone(res.data.timeZone);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load open times.');
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [selectedProperty, selectedUnit]);

  useEffect(() => {
    setSelectedDay('');
    setSelectedSlot(null);
    if (selectedProperty) fetchSlots();
  }, [selectedProperty, selectedUnit, fetchSlots]);

  const days = useMemo(() => groupSlotsByDay(slots, timeZone), [slots, timeZone]);
  const timesForDay = useMemo(
    () => days.find((d) => d.dateKey === selectedDay)?.slots || [],
    [days, selectedDay]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) {
      setError('Please pick a day and time.');
      return;
    }
    if (!formData.contactName.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!formData.contactEmail.trim() && !formData.contactPhone.trim()) {
      setError('Please enter your email or phone number.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const body = {
        propertyId: selectedProperty,
        unitId: selectedUnit || null,
        scheduledAt: selectedSlot.start,
        contactName: formData.contactName.trim(),
        contactEmail: formData.contactEmail.trim() || null,
        contactPhone: formData.contactPhone.trim() || null,
        notes: formData.notes.trim(),
      };
      if (bookingSlug) body.bookingSlug = bookingSlug;
      const res = await api.post('/api/showings/public/book', body);
      setBooked(res.data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          'Could not book that time. Please try another slot.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (pageError) {
    return (
      <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
        <Alert severity="error">{pageError}</Alert>
      </Box>
    );
  }

  if (booked) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 560, mx: 'auto' }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Showing requested
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {booked.message ||
                `${landlordName} will confirm your time. Watch for an email when it is set.`}
            </Typography>
            {booked.when && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                {booked.when}
              </Typography>
            )}
            {booked.propertyName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {[booked.propertyName, booked.unitName].filter(Boolean).join(', unit ')}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Check your email for a link to reschedule or cancel.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const step = !selectedProperty ? 1 : !selectedSlot ? 2 : 3;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Book a showing
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        with {landlordName} · about {showingMinutes} minutes
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Property + unit */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                {step >= 1 ? '1. Property' : 'Property'}
              </Typography>
              {properties.length > 1 ? (
                <FormControl fullWidth sx={{ mb: units.length ? 2 : 0 }}>
                  <InputLabel>Property</InputLabel>
                  <Select
                    value={selectedProperty}
                    label="Property"
                    onChange={(e) => {
                      setSelectedProperty(e.target.value);
                      setSelectedUnit('');
                    }}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    {properties.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {propertyLabel(p)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2" sx={{ mb: units.length ? 2 : 0 }}>
                  {propertyLabel(selectedPropertyData)}
                </Typography>
              )}
              {selectedProperty && units.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Unit (optional)</InputLabel>
                  <Select
                    value={selectedUnit || ANY_UNIT}
                    label="Unit (optional)"
                    onChange={(e) => setSelectedUnit(e.target.value === ANY_UNIT ? '' : e.target.value)}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    <MenuItem value={ANY_UNIT}>Any / whole property</MenuItem>
                    {units.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name || u.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Day + time */}
        <Grid item xs={12} md={4}>
          <Card sx={{ minHeight: 280 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                2. Day & time
              </Typography>
              {!selectedProperty ? (
                <Alert severity="info">Pick a property first.</Alert>
              ) : loadingSlots ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : days.length === 0 ? (
                <Alert severity="warning">
                  No open times in the next 30 days. Try another unit or contact {landlordName}.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Choose a day
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 2, maxHeight: 160, overflowY: 'auto' }}>
                    {days.map((day) => (
                      <Grid item xs={12} key={day.dateKey}>
                        <Button
                          fullWidth
                          size="small"
                          variant={selectedDay === day.dateKey ? 'contained' : 'outlined'}
                          onClick={() => {
                            setSelectedDay(day.dateKey);
                            setSelectedSlot(null);
                          }}
                          sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                        >
                          {day.label}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                  {selectedDay && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Choose a time
                      </Typography>
                      <Grid container spacing={1}>
                        {timesForDay.map((slot) => (
                          <Grid item xs={4} key={slot.start}>
                            <Button
                              fullWidth
                              size="small"
                              variant={selectedSlot?.start === slot.start ? 'contained' : 'outlined'}
                              onClick={() => setSelectedSlot(slot)}
                              sx={{ textTransform: 'none' }}
                            >
                              {formatSlotTime(slot.start, timeZone)}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Details */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                3. Your details
              </Typography>
              {selectedSlot ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {formatSlotDateTime(selectedSlot.start, timeZone)}
                </Typography>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Pick a day and time to continue.
                </Alert>
              )}
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  required
                  label="Your name"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  sx={{ mb: 2 }}
                  size={isMobile ? 'small' : 'medium'}
                  disabled={!selectedSlot}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  sx={{ mb: 2 }}
                  size={isMobile ? 'small' : 'medium'}
                  disabled={!selectedSlot}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  sx={{ mb: 2 }}
                  size={isMobile ? 'small' : 'medium'}
                  disabled={!selectedSlot}
                />
                <TextField
                  fullWidth
                  label="Notes (optional)"
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  sx={{ mb: 2 }}
                  size={isMobile ? 'small' : 'medium'}
                  disabled={!selectedSlot}
                />
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
                  Email or phone is required so we can confirm your showing.
                </Alert>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={!selectedSlot || submitting}
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CalendarIcon />}
                >
                  Request showing
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ShowingBooking;
